import { useQuery } from "@tanstack/react-query";
import { Link, useOutletContext } from "react-router-dom";
import { meApi } from "../../api/me";
import type { MyProduct, MyProductOrg, UserOut } from "../../api/types";
import { formatCredits, formatDateTime } from "../../shared/format";
import { useAdminWorkspace } from "../admin/useAdminWorkspace";
import { Badge, Button, Card, Spinner } from "../../ui";

function roleLine(org: MyProductOrg): string {
  if (org.kind === "personal") return "Your personal account";
  const role = org.role === "owner" ? "owner" : org.role;
  const people = org.members === 1 ? "1 member" : `${org.members} members`;
  return `${org.name} · you are ${role} · ${people}`;
}

function ProductCard({ product }: { product: MyProduct }) {
  const bills = product.capabilities.includes("billing");
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {product.logo_url ? (
          <img src={product.logo_url} alt="" className="h-9 w-9 rounded-md" />
        ) : (
          <span className="inline-block h-9 w-9 rounded-md bg-brand" />
        )}
        <div className="flex-1">
          <p className="font-semibold tracking-tight">{product.name}</p>
          {product.last_seen_at ? (
            <p className="text-xs text-slate-500">
              Last used {formatDateTime(product.last_seen_at)}
            </p>
          ) : null}
        </div>
        {product.web_url ? (
          <Button
            onClick={() => window.location.assign(product.web_url as string)}
          >
            Open {product.name}
          </Button>
        ) : null}
      </div>
      <ul className="flex flex-col gap-2 text-sm">
        {product.orgs.map((org) => (
          <li
            key={org.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2"
          >
            <span>
              <Link
                className="text-brand underline"
                to="/account/organizations"
              >
                {roleLine(org)}
              </Link>
              {org.billing_active ? null : (
                <Badge tone="warning">billing paused</Badge>
              )}
            </span>
            {bills && org.balance !== null ? (
              <span className="font-medium">
                {formatCredits(org.balance)} credits
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** "Who am I and what am I part of", answered before anyone clicks (docs/15 §2). */
export function AccountHome() {
  const me = useOutletContext<UserOut>();
  const products = useQuery({
    queryKey: ["me", "products"],
    queryFn: () => meApi.products(),
  });
  const workspace = useAdminWorkspace();

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold tracking-tight">
          {me.first_name} {me.last_name ?? ""}
        </h1>
        <p className="text-sm text-slate-600">
          {me.email}{" "}
          {me.email_verified ? (
            <Badge tone="success">verified</Badge>
          ) : (
            <Badge tone="warning">unverified</Badge>
          )}
        </p>
        <p className="text-sm text-slate-600">
          Sign in with{" "}
          {[
            me.has_password ? "a password" : null,
            ...me.identities.map((i) => i.provider),
            me.passkeys > 0 ? `${me.passkeys} passkeys` : null,
          ]
            .filter(Boolean)
            .join(", ") || "no method yet"}
          {me.mfa_enabled ? " · two-factor on" : " · two-factor off"}
        </p>
        <div>
          <Link className="text-sm text-brand underline" to="/account/security">
            Security settings
          </Link>
        </div>
      </Card>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Your products
        </h2>
        {products.isLoading ? <Spinner /> : null}
        {products.data?.map((product) => (
          <ProductCard key={product.slug} product={product} />
        ))}
        {products.data && products.data.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-600">
              You are not using any Intelibo product yet.
            </p>
          </Card>
        ) : null}
      </section>

      {workspace.isStaff ? (
        <Card className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Staff access
          </h2>
          {workspace.platformRole === "owner" ? (
            <Link className="text-sm text-brand underline" to="/admin/platform">
              Platform owner · every product
            </Link>
          ) : null}
          {workspace.products.map((p) => (
            <Link
              key={p.slug}
              className="text-sm text-brand underline"
              to={`/admin/p/${p.slug}`}
            >
              {p.role === "owner"
                ? "Owner"
                : p.role === "admin"
                  ? "Admin"
                  : "Support"}{" "}
              for {p.name}
            </Link>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
